import {QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {GoogleAuthProvider, onAuthStateChanged, signInWithPopup, User} from 'firebase/auth';
import {BriefcaseBusiness, CircleAlert, LogIn, RefreshCw} from 'lucide-react';
import {useEffect, useState} from 'react';
import {BrowserRouter, Navigate, Route, Routes} from './router';
import {ApiError, api, getPursuitDeckAuth, signOutPursuitDeck} from './api';
import {BetaNotice} from './components/BetaNotice';
import {Onboarding} from './components/Onboarding';
import {Shell} from './components/Shell';
import {PursuitDeckDesignProvider, PrimaryButton, SecondaryButton, Surface} from './design';
import {AuditHistoryPage} from './pages/AuditHistoryPage';
import {ActionInboxPage} from './pages/ActionInboxPage';
import {CareerProfilePage} from './pages/CareerProfileWorkspacePage';
import {DiscoverPage} from './pages/DiscoverPage';
import {DocumentsPage} from './pages/DocumentsPage';
import {OpportunitiesPage} from './pages/OpportunitiesPage';
import {OpportunityDetailPage} from './pages/OpportunityDetailPage';
import {RelationshipsPage} from './pages/RelationshipsPage';
import {SettingsRoutePage} from './pages/SettingsPage';
import {PrivacyRoutePage} from './pages/PrivacyPage';
import {OnboardingState} from './types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
    mutations: {retry: false},
  },
});

export const requiresOnboarding = (onboarding: OnboardingState): boolean =>
  !onboarding.profileReady && !onboarding.reducedQualityMode;

const AuthState = ({children}: {children: (user: User | null) => React.ReactNode}) => {
  const [state, setState] = useState<{ready: boolean; user: User | null}>({ready: false, user: null});

  useEffect(() => {
    let unsubscribe: () => void = () => undefined;
    getPursuitDeckAuth()
      .then((auth) => {
        unsubscribe = onAuthStateChanged(auth, (user) => setState({ready: true, user}));
      })
      .catch(() => setState({ready: true, user: null}));
    return () => unsubscribe();
  }, []);

  if (!state.ready) {
    return (
      <main className="pd-state-page" aria-busy="true">
        <div className="pd-loading-mark" aria-hidden="true">
          PD
        </div>
        <p>Opening your search desk…</p>
      </main>
    );
  }
  return <>{children(state.user)}</>;
};

const LoginRequired = () => {
  const [error, setError] = useState<string>();
  const [signingIn, setSigningIn] = useState(false);

  const signIn = async () => {
    setSigningIn(true);
    setError(undefined);
    try {
      await signInWithPopup(await getPursuitDeckAuth(), new GoogleAuthProvider());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Google sign-in could not be completed.');
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <main className="pd-state-page">
      <Surface className="pd-state-card">
        <span className="pd-eyebrow">Private beta</span>
        <BriefcaseBusiness size={40} aria-hidden="true" />
        <h1>PursuitDeck is ready when you are.</h1>
        <p>Sign in with your invited Google account to open your private job-search workspace.</p>
        <a className="pd-text-link" href="/privacy">Privacy and data handling</a>
        <PrimaryButton
          loading={signingIn}
          onClick={() => void signIn()}
          leftSection={<LogIn size={17} aria-hidden="true" />}
        >
          Sign in with Google
        </PrimaryButton>
        {error ? <p className="pd-form-error" role="alert">{error}</p> : null}
      </Surface>
    </main>
  );
};

const AccessDenied = () => (
  <main className="pd-state-page">
    <Surface className="pd-state-card">
      <span className="pd-eyebrow">Invitation required</span>
      <CircleAlert size={40} aria-hidden="true" />
      <h1>This workspace is not on your keyring yet.</h1>
      <p>
        Your PursuitDeck account is still signed in, but PursuitDeck is currently limited to invited beta
        candidates.
      </p>
      <PrimaryButton onClick={() => void signOutPursuitDeck()}>
        Sign out
      </PrimaryButton>
    </Surface>
  </main>
);

const OnboardingRoute = ({onboarding}: {onboarding: OnboardingState}) => {
  const client = useQueryClient();
  const resume = useMutation({
    mutationFn: () => onboarding.profileReady
      ? api.reopenOnboarding()
      : api.setReducedQualityMode(false),
    onSuccess: async () => {
      await client.invalidateQueries({queryKey: ['bootstrap']});
    },
  });

  if (!onboarding.profileReady && !onboarding.reducedQualityMode) {
    return <Onboarding onboarding={onboarding} embedded />;
  }

  return (
    <Surface className="pd-onboarding-reentry">
      <span className="pd-eyebrow">Career interview</span>
      <h2>{onboarding.profileReady ? 'Revisit the story behind your resume.' : 'Finish the interview when you are ready.'}</h2>
      <p>
        {onboarding.profileReady
          ? 'PursuitDeck will reuse the existing two-round question bundles, preserve your source history, and update only the answers you explicitly resubmit.'
          : 'Your saved source material and question progress are still here. Resume the interview to improve search and fit recommendations.'}
      </p>
      <PrimaryButton
        loading={resume.isPending}
        onClick={() => resume.mutate()}
      >
        {onboarding.profileReady ? 'Reopen career interview' : 'Resume career interview'}
      </PrimaryButton>
      {resume.isError ? (
        <p className="pd-form-error" role="alert">
          {resume.error instanceof Error ? resume.error.message : 'The career interview could not be reopened.'}
        </p>
      ) : null}
    </Surface>
  );
};

const Workspace = () => {
  const bootstrap = useQuery({queryKey: ['bootstrap'], queryFn: api.bootstrap});

  if (bootstrap.isLoading) {
    return (
      <main className="pd-state-page" aria-busy="true">
        <div className="pd-loading-mark" aria-hidden="true">
          PD
        </div>
        <p>Assembling opportunities, promises, and next moves…</p>
      </main>
    );
  }

  if (bootstrap.error instanceof ApiError && bootstrap.error.status === 403) return <AccessDenied />;

  if (bootstrap.isError || !bootstrap.data) {
    return (
      <main className="pd-state-page">
        <Surface className="pd-state-card">
          <CircleAlert size={40} aria-hidden="true" />
          <h1>Your desk could not be opened.</h1>
          <p>{bootstrap.error instanceof Error ? bootstrap.error.message : 'Try again in a moment.'}</p>
          <SecondaryButton
            leftSection={<RefreshCw size={17} aria-hidden="true" />}
            onClick={() => void bootstrap.refetch()}
          >
            Try again
          </SecondaryButton>
        </Surface>
      </main>
    );
  }

  const onboarding = bootstrap.data.onboarding;
  return (
    <>
      <BetaNotice opened={!onboarding.betaNoticeAcceptedAt} />
      {requiresOnboarding(onboarding) ? (
        <Onboarding onboarding={onboarding} />
      ) : (
        <Shell bootstrap={bootstrap.data}>
          <Routes>
            <Route path="onboarding" element={<OnboardingRoute onboarding={onboarding} />} />
            <Route index element={<OpportunitiesPage />} />
            <Route path="opportunities/:id" element={<OpportunityDetailPage />} />
            <Route path="action-inbox" element={<ActionInboxPage />} />
            <Route path="discover" element={<DiscoverPage />} />
            <Route path="relationships" element={<RelationshipsPage />} />
            <Route path="career-profile" element={<CareerProfilePage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="settings" element={<SettingsRoutePage />} />
            <Route path="history" element={<AuditHistoryPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Shell>
      )}
    </>
  );
};

export const App = () => (
  <PursuitDeckDesignProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/privacy" element={<PrivacyRoutePage />} />
          <Route
            path="*"
            element={<AuthState>{(user) => (user ? <Workspace /> : <LoginRequired />)}</AuthState>}
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </PursuitDeckDesignProvider>
);
